import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import { ArrowLeft, Download, AlertCircle, FileText, Loader2 } from 'lucide-react';

/**
 * Phase B — Evidence Preview as a routed page (was a shadcn Dialog).
 * Route: /cases/:caseId/evidence/:evidenceId/preview
 * Loads the evidence record + inline preview blob and renders it fullscreen
 * inside the NxShell workspace. Uses browser Back to close.
 */
const EvidencePreviewPage = () => {
  const { caseId, evidenceId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('nexus_token');

  const [evidence, setEvidence] = useState(null);
  const [blobUrl, setBlobUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch metadata
      const metaRes = await fetch(`${BACKEND_URL}/api/evidence/${evidenceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!metaRes.ok) throw new Error(`Could not load evidence (${metaRes.status})`);
      const meta = await metaRes.json();
      setEvidence(meta);

      // Fetch blob
      const viewRes = await fetch(`${BACKEND_URL}/api/evidence/${evidenceId}/view`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!viewRes.ok) {
        const err = await viewRes.json().catch(() => ({}));
        throw new Error(err?.detail || `Preview failed (${viewRes.status})`);
      }
      const blob = await viewRes.blob();
      setBlobUrl(URL.createObjectURL(blob));
    } catch (e) {
      setError(e.message || 'Could not load preview');
    } finally {
      setLoading(false);
    }
  }, [evidenceId, token]);

  useEffect(() => {
    loadPreview();
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [evidenceId]);

  const handleDownload = async () => {
    if (!evidence) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/evidence/${evidence.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = evidence.file_name || `evidence-${evidence.id}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Download failed', description: e.message, variant: 'destructive' });
    }
  };

  const handleBack = () => {
    if (caseId) navigate(`/cases/${caseId}?tab=evidence`);
    else navigate(-1);
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex items-center gap-2 text-slate-300">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading preview…
        </div>
      );
    }
    if (error) {
      const attemptRecovery = async () => {
        try {
          const r = await fetch(`${BACKEND_URL}/api/evidence/${evidenceId}/attempt-recovery`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` },
          });
          const d = await r.json().catch(() => ({}));
          if (d.recovered) {
            toast({ title: 'File recovered', description: d.reason?.replace(/_/g, ' ') || 'Restored from backup' });
            loadPreview();
          } else {
            toast({ title: 'Could not auto-recover', description: (d.reason || 'file missing').replace(/_/g, ' '), variant: 'destructive' });
          }
        } catch (e) {
          toast({ title: 'Recovery failed', description: e.message, variant: 'destructive' });
        }
      };
      const triggerManualReupload = () => {
        const inp = document.createElement('input');
        inp.type = 'file';
        inp.onchange = async () => {
          const f = inp.files?.[0];
          if (!f) return;
          try {
            const fd = new FormData(); fd.append('file', f);
            const r = await fetch(`${BACKEND_URL}/api/evidence/${evidenceId}/manual-reupload`, {
              method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.detail || 'Re-upload failed');
            toast({
              title: 'Re-upload stored',
              description: d.verification === 'HASH_VERIFIED'
                ? 'SHA-256 matched the original — file verified.'
                : d.verification === 'HASH_MISMATCH'
                  ? 'Hash mismatch — saved as a new version for admin review.'
                  : 'File saved. Original hash was not available for verification.',
              variant: d.verification === 'HASH_MISMATCH' ? 'destructive' : 'default',
            });
            loadPreview();
          } catch (e) {
            toast({ title: 'Re-upload failed', description: e.message, variant: 'destructive' });
          }
        };
        inp.click();
      };
      return (
        <div className="text-center p-8 max-w-md">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
          <p className="text-amber-300 font-semibold mb-1">Preview unavailable</p>
          <p className="text-slate-400 text-sm">{error}</p>
          <div className="flex gap-2 justify-center mt-4 flex-wrap">
            <Button onClick={attemptRecovery} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold" data-testid="evidence-preview-recover">
              Auto-Recover
            </Button>
            <Button onClick={loadPreview} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" data-testid="evidence-preview-retry">
              Retry
            </Button>
            <Button onClick={triggerManualReupload} variant="outline" className="border-amber-400/60 text-amber-200 hover:bg-amber-500/10" data-testid="evidence-preview-manual-reupload">
              Manual Re-upload
            </Button>
          </div>
          <p className="text-[10px] text-slate-500 mt-3">
            Manual re-upload verifies the new file's SHA-256 against the original and stores a new version. The original hash is never overwritten.
          </p>
        </div>
      );
    }
    if (!blobUrl || !evidence) return null;
    const ct = (evidence.content_type || '').toLowerCase();
    const name = (evidence.file_name || '').toLowerCase();
    const isImg = ct.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)$/.test(name);
    const isVid = ct.startsWith('video/') || /\.(mp4|mov|webm|avi|wmv)$/.test(name);
    const isAud = ct.startsWith('audio/') || /\.(mp3|wav|ogg|aac|flac)$/.test(name);
    const isPdf = ct === 'application/pdf' || name.endsWith('.pdf');
    if (isImg) {
      return (
        <img
          src={blobUrl}
          alt={evidence.file_name}
          style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }}
          data-testid="evidence-preview-image"
        />
      );
    }
    if (isVid) {
      return (
        <video
          src={blobUrl}
          controls
          style={{ maxWidth: '100%', maxHeight: '75vh' }}
          data-testid="evidence-preview-video"
        />
      );
    }
    if (isAud) return <audio src={blobUrl} controls className="w-full" data-testid="evidence-preview-audio" />;
    if (isPdf) {
      return (
        <iframe
          src={blobUrl}
          title="pdf-preview"
          style={{ width: '100%', height: '75vh', border: 0 }}
          data-testid="evidence-preview-pdf"
        />
      );
    }
    return (
      <div className="text-center p-8">
        <FileText className="w-12 h-12 text-slate-500 mx-auto mb-3" />
        <p className="text-white text-sm mb-1">Preview not supported for this file type.</p>
        <p className="text-slate-400 text-xs">Download the file to open it in its native app.</p>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="evidence-preview-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="outline"
            className="border-[#334155] text-slate-200 hover:bg-[#182136]"
            onClick={handleBack}
            data-testid="evidence-preview-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-white truncate">
              {evidence?.file_name || 'Evidence Preview'}
            </h1>
            <div className="flex flex-wrap gap-2 items-center text-xs text-slate-400 mt-1">
              {evidence?.evidence_type && (
                <Badge className="bg-[#182136] text-slate-300 border border-[#334155]">
                  {evidence.evidence_type}
                </Badge>
              )}
              {evidence?.content_type && <span className="font-mono">{evidence.content_type}</span>}
              {evidence?.file_size && (
                <span className="font-mono">{(evidence.file_size / 1024).toFixed(1)} KB</span>
              )}
            </div>
          </div>
        </div>
        <Button
          onClick={handleDownload}
          disabled={!evidence}
          className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white"
          data-testid="evidence-preview-download-btn"
        >
          <Download className="w-4 h-4 mr-2" /> Download
        </Button>
      </div>

      <div
        className="bg-[#050913] rounded-lg border border-[#1E293B] flex items-center justify-center p-4 nx-inner-scroll-keep"
        style={{ minHeight: '400px' }}
        data-testid="evidence-preview-body"
      >
        {renderBody()}
      </div>

      {evidence?.description && (
        <div className="bg-[#131A2A] border border-[#334155] rounded-lg p-4">
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Description</p>
          <p className="text-sm text-slate-200 whitespace-pre-wrap">{evidence.description}</p>
        </div>
      )}
    </div>
  );
};

export default EvidencePreviewPage;
